import Transform from '../math/Transform.js';
import DOMUtils from '../utils/DOMUtils.js';

/**
 * WaterIntersectionManager - Handles detection and clipping of sprites
 * when they intersect with non-collider transparent cuboids (like water).
 *
 * Clipping approach (z-buffer style for billboards):
 * - Project the water face corners to screen space
 * - Build clip polygons using the actual projected face edges
 * - Water surface = horizontal clip, water walls = diagonal clip following isometric angle
 */
export default class WaterIntersectionManager {
  constructor(world) {
    this.world = world;
  }

  /**
   * Find water cuboids (noCollision + transparent) that intersect with a character
   */
  findIntersectingWaterCuboids(character) {
    const collisionSystem = this.world.collisionSystem;
    if (!collisionSystem) return [];

    const halfWidth = character.width / 2;
    const halfDepth = character.depth / 2;
    const charAABB = {
      minX: character.x - halfWidth,
      minY: character.y,
      minZ: character.z - halfDepth,
      maxX: character.x + halfWidth,
      maxY: character.y + character.height,
      maxZ: character.z + halfDepth
    };

    const candidates = collisionSystem.queryCuboids(charAABB);
    const waterCuboids = [];

    for (const cuboid of candidates) {
      if (!cuboid.hasNoCollision || !cuboid.hasNoCollision()) continue;
      if (!cuboid.hasNonOpaqueFace || !cuboid.hasNonOpaqueFace()) continue;

      const cuboidAABB = {
        minX: cuboid.x,
        minY: cuboid.y,
        minZ: cuboid.z,
        maxX: cuboid.x + cuboid.width,
        maxY: cuboid.y + cuboid.height,
        maxZ: cuboid.z + cuboid.depth
      };

      if (this.aabbIntersects(charAABB, cuboidAABB)) {
        waterCuboids.push(cuboid);
      }
    }

    return waterCuboids;
  }


  aabbIntersects(a, b) {
    return (
      a.minX < b.maxX && a.maxX > b.minX &&
      a.minY < b.maxY && a.maxY > b.minY &&
      a.minZ < b.maxZ && a.maxZ > b.minZ
    );
  }

  aabbIntersection(a, b) {
    return {
      minX: Math.max(a.minX, b.minX),
      minY: Math.max(a.minY, b.minY),
      minZ: Math.max(a.minZ, b.minZ),
      maxX: Math.min(a.maxX, b.maxX),
      maxY: Math.min(a.maxY, b.maxY),
      maxZ: Math.min(a.maxZ, b.maxZ)
    };
  }

  /**
   * Group water cuboids into connected volumes using Union-Find.
   * Two cuboids are "connected" if they touch (share a face, edge, or corner).
   * @param {Cuboid[]} waterCuboids - All intersecting water cuboids
   * @returns {Cuboid[][]} Array of connected water volumes
   */
  groupConnectedWaterVolumes(waterCuboids) {
    if (waterCuboids.length <= 1) return [waterCuboids];

    // Union-Find with path compression
    const parent = new Map();
    const find = (c) => {
      if (!parent.has(c)) parent.set(c, c);
      if (parent.get(c) !== c) parent.set(c, find(parent.get(c)));
      return parent.get(c);
    };
    const union = (a, b) => {
      const ra = find(a), rb = find(b);
      if (ra !== rb) parent.set(ra, rb);
    };

    // Two cuboids touch if AABBs overlap or are adjacent (>= not >)
    // For vertically-adjacent cuboids (Y-touching but not Y-overlapping),
    // only group if they share the same XZ footprint — different footprints
    // need separate clip calculations (band clipping).
    const touching = (a, b) => {
      const xTouch = a.x + a.width >= b.x && b.x + b.width >= a.x;
      const yTouch = a.y + a.height >= b.y && b.y + b.height >= a.y;
      const zTouch = a.z + a.depth >= b.z && b.z + b.depth >= a.z;
      if (!xTouch || !yTouch || !zTouch) return false;

      const yOverlap = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
      if (yOverlap <= 0.01) {
        // Y-adjacent only: group only if XZ footprints match exactly
        return a.x === b.x && a.z === b.z && a.width === b.width && a.depth === b.depth;
      }
      return true;
    };

    for (let i = 0; i < waterCuboids.length; i++) {
      for (let j = i + 1; j < waterCuboids.length; j++) {
        if (touching(waterCuboids[i], waterCuboids[j])) {
          union(waterCuboids[i], waterCuboids[j]);
        }
      }
    }

    // Collect groups
    const groups = new Map();
    for (const c of waterCuboids) {
      const root = find(c);
      if (!groups.has(root)) groups.set(root, []);
      groups.get(root).push(c);
    }
    return Array.from(groups.values());
  }

  /**
   * Generate a stable ID for a water volume (for fragment tracking/reuse).
   * @param {Cuboid[]} volumeCuboids - Cuboids in this volume
   * @returns {string} Stable volume ID
   */
  getVolumeId(volumeCuboids) {
    return volumeCuboids
      .map(c => `${c.x},${c.y},${c.z}`)
      .sort()
      .join('|');
  }

  /**
   * Get sprite's screen-space bounding box
   */
  getSpriteScreenBounds(character, camera) {
    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const effectiveCubeSize = camera.CUBE_SIZE * zoom;

    const charCenterX = character.x * effectiveCubeSize;
    const charCenterZ = -character.z * effectiveCubeSize;
    const charFeetY = character.y * effectiveCubeSize;
    const charHeadY = (character.y + character.height) * effectiveCubeSize;

    const headScreen = Transform.worldToScreen(
      charCenterX, charHeadY, charCenterZ, camera,
      isAndroid ? zoom : 1
    );
    const feetScreen = Transform.worldToScreen(
      charCenterX, charFeetY, charCenterZ, camera,
      isAndroid ? zoom : 1
    );

    const top = headScreen.y;
    const bottom = feetScreen.y;
    const height = bottom - top;
    const centerX = headScreen.x;

    const sprite = character.sprite;
    let width;
    if (sprite) {
      const spriteOffset = sprite.spriteOffset || { scale: 1 };
      const scaleMultiplier = spriteOffset.scale || 1;
      const targetHeight = character.height * effectiveCubeSize * scaleMultiplier;
      const spriteScale = targetHeight / sprite.spriteHeight;
      width = sprite.spriteWidth * spriteScale;
    } else {
      width = height * 0.8;
    }

    return {
      left: centerX - width / 2,
      right: centerX + width / 2,
      top,
      bottom,
      width,
      height,
      centerX
    };
  }

  /**
   * Project a 3D point to screen space and convert to sprite-relative percentage
   */
  projectToSpritePercent(worldX, worldY, worldZ, spriteBounds, camera, cubeSize, zoomParam) {
    const screen = Transform.worldToScreen(
      worldX * cubeSize,
      worldY * cubeSize,
      -worldZ * cubeSize,
      camera,
      zoomParam
    );

    return {
      x: ((screen.x - spriteBounds.left) / spriteBounds.width) * 100,
      y: ((screen.y - spriteBounds.top) / spriteBounds.height) * 100,
      screenX: screen.x,
      screenY: screen.y
    };
  }

  /**
   * Calculate clip information for a character in water
   */
  calculateClipInfo(character, camera) {
    const waterCuboids = this.findIntersectingWaterCuboids(character);
    if (waterCuboids.length === 0) return null;

    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const cubeSize = camera.CUBE_SIZE * zoom;
    const zoomParam = isAndroid ? zoom : 1;

    const halfWidth = character.width / 2;
    const halfDepth = character.depth / 2;

    const charAABB = {
      minX: character.x - halfWidth,
      minY: character.y,
      minZ: character.z - halfDepth,
      maxX: character.x + halfWidth,
      maxY: character.y + character.height,
      maxZ: character.z + halfDepth
    };

    // Calculate union of all water intersections
    let unionMinX = Infinity, unionMinY = Infinity, unionMinZ = Infinity;
    let unionMaxX = -Infinity, unionMaxY = -Infinity, unionMaxZ = -Infinity;

    for (const cuboid of waterCuboids) {
      const waterAABB = {
        minX: cuboid.x, minY: cuboid.y, minZ: cuboid.z,
        maxX: cuboid.x + cuboid.width,
        maxY: cuboid.y + cuboid.height,
        maxZ: cuboid.z + cuboid.depth
      };

      const intersection = this.aabbIntersection(charAABB, waterAABB);
      unionMinX = Math.min(unionMinX, intersection.minX);
      unionMinY = Math.min(unionMinY, intersection.minY);
      unionMinZ = Math.min(unionMinZ, intersection.minZ);
      unionMaxX = Math.max(unionMaxX, intersection.maxX);
      unionMaxY = Math.max(unionMaxY, intersection.maxY);
      unionMaxZ = Math.max(unionMaxZ, intersection.maxZ);
    }

    if (unionMaxX - unionMinX <= 0.01 || unionMaxY - unionMinY <= 0.01 || unionMaxZ - unionMinZ <= 0.01) {
      return null;
    }

    const spriteBounds = this.getSpriteScreenBounds(character, camera);
    const surfaceY = unionMaxY;

    // Calculate which edges the character crosses
    const EDGE_THRESHOLD = 0.01;
    const crossesMinX = unionMinX > charAABB.minX + EDGE_THRESHOLD;
    const crossesMaxX = unionMaxX < charAABB.maxX - EDGE_THRESHOLD;
    const crossesMinZ = unionMinZ > charAABB.minZ + EDGE_THRESHOLD;
    const crossesMaxZ = unionMaxZ < charAABB.maxZ - EDGE_THRESHOLD;

    // Calculate horizontal clip percentage (water surface)
    // This is based purely on world Y coordinates, NOT screen projection.
    // The sprite is a flat 2D image where Y represents character height (head=0%, feet=100%).
    // The clip percentage is simply where the water surface falls within the character's height.
    // This is independent of camera angle and skew.
    const characterHeadY = character.y + character.height;
    const characterFeetY = character.y;

    // Clamp surface to character bounds to get valid percentage
    const clampedSurfaceY = Math.max(characterFeetY, Math.min(characterHeadY, surfaceY));
    // Raw clip percent in collision-box coordinate space (needed for edge intersection math)
    const rawClipPercent = ((characterHeadY - clampedSurfaceY) / character.height) * 100;

    // Adjust for sprite Y offset: different character models (bear, hellrat, LPC) position
    // the sprite element differently. Compensate so the clip line stays aligned with the
    // actual water surface on the visible character.
    const spriteOffsetY = character.sprite?.spriteOffset?.y ?? -0.88;
    const offsetDelta = spriteOffsetY - (-0.88); // difference from default humanoid offset
    let horizontalClipPercent = rawClipPercent;
    if (Math.abs(offsetDelta) > 0.001) {
      horizontalClipPercent += (offsetDelta / character.height) * 100;
    }

    // If fully inside water horizontally, use simple horizontal clip
    if (!crossesMinX && !crossesMaxX && !crossesMinZ && !crossesMaxZ) {
      if (horizontalClipPercent <= 0.1) {
        // Fully submerged - entire sprite is underwater
        return {
          waterCuboids,
          clipTopPercent: 0,
          isSimpleHorizontalClip: true,
          isFullySubmerged: true,
          surfaceY
        };
      }

      return {
        waterCuboids,
        clipTopPercent: horizontalClipPercent,
        isSimpleHorizontalClip: true,
        surfaceY
      };
    }

    // Character crosses water edges - need to build proper clip polygon
    // Project the water face vertices to sprite coordinates

    const waterBounds = {
      minX: unionMinX, maxX: unionMaxX,
      minZ: unionMinZ, maxZ: unionMaxZ
    };

    // IMPORTANT: Pass rawClipPercent (un-adjusted) to buildEdgeClipData so the
    // horizontal line and projected edge points are in the same coordinate system
    // (collision-box space). The offset adjustment is applied afterward to the
    // stored horizontalClipPercent for correct CSS rendering.
    const clipData = this.buildEdgeClipData(
      character, charAABB, waterBounds, surfaceY,
      spriteBounds, camera, cubeSize, zoomParam,
      { crossesMinX, crossesMaxX, crossesMinZ, crossesMaxZ },
      rawClipPercent
    );
    // Apply the offset to the stored clip percent used by polygon builders
    clipData.horizontalClipPercent = horizontalClipPercent;

    return {
      waterCuboids,
      clipTopPercent: horizontalClipPercent,
      isSimpleHorizontalClip: false,
      clipData,
      surfaceY
    };
  }

  /**
   * Determine if a side face is visible based on camera angle
   * @param {string} face - 'minX', 'maxX', 'minZ', or 'maxZ'
   * @param {Object} camera - Camera object with angle property
   * @returns {boolean} True if the face is visible
   */
  isSideFaceVisible(face, camera) {
    const rotation = Transform.getCameraRotation(camera);
    const sin = rotation.sin;
    const cos = rotation.cos;

    // Face visibility based on camera rotation:
    // A face is visible when its outward normal points toward the camera
    // Camera looks in direction determined by angle - faces pointing back at camera are visible
    switch (face) {
      case 'minX': return sin < 0;  // Face normal (-1, 0, 0) visible when looking from -X
      case 'maxX': return sin > 0;  // Face normal (+1, 0, 0) visible when looking from +X
      case 'minZ': return cos > 0;  // Face normal (0, 0, -1) visible when looking from -Z
      case 'maxZ': return cos < 0;  // Face normal (0, 0, +1) visible when looking from +Z
      default: return false;
    }
  }

  /**
   * Build clip data for edge crossings
   *
   * Key insight: The horizontal cut at water surface is correct. We need to find
   * where this horizontal line INTERSECTS the top face edge (which appears diagonal
   * in isometric). At that intersection point, we add a vertical cut.
   *
   * IMPORTANT: If the crossed edge's side face is NOT visible (a "back edge"),
   * the "outside water" portion is actually BEHIND the water from the camera's view.
   * In that case, we treat the entire below-surface region as submerged.
   *
   * This creates up to 4 regions:
   * - Above surface (main sprite)
   * - Below surface + inside top face (submerged, behind water faces)
   * - Below surface + outside top face, front edge (NOT submerged, in front of water)
   * - Below surface + outside top face, back edge (submerged, behind water)
   */
  buildEdgeClipData(character, charAABB, waterBounds, surfaceY,
                    spriteBounds, camera, cubeSize, zoomParam,
                    crosses, horizontalClipPercent) {

    const rotation = Transform.getCameraRotation(camera);
    const edgeData = [];

    // Determine which X edge is crossed (if any)
    const crossedXEdge = crosses.crossesMinX ? 'minX' : (crosses.crossesMaxX ? 'maxX' : null);
    // Determine which Z edge is crossed (if any)
    const crossedZEdge = crosses.crossesMinZ ? 'minZ' : (crosses.crossesMaxZ ? 'maxZ' : null);

    // Track if all crossed edges are back edges (no visible cuts needed)
    let allBackEdges = true;

    // Process X edge if crossed
    if (crossedXEdge) {
      const isFrontEdge = this.isSideFaceVisible(crossedXEdge, camera);

      if (isFrontEdge) {
        allBackEdges = false;

        // Project the X edge (runs along Z)
        const xEdgeX = crossedXEdge === 'minX' ? waterBounds.minX : waterBounds.maxX;
        const edgePoint1 = this.projectToSpritePercent(
          xEdgeX, surfaceY, waterBounds.minZ,
          spriteBounds, camera, cubeSize, zoomParam
        );
        const edgePoint2 = this.projectToSpritePercent(
          xEdgeX, surfaceY, waterBounds.maxZ,
          spriteBounds, camera, cubeSize, zoomParam
        );

        const intersectionX = this.findLineHorizontalIntersection(
          edgePoint1.x, edgePoint1.y,
          edgePoint2.x, edgePoint2.y,
          horizontalClipPercent
        );

        // For X edges: inside water direction based on cos
        const waterOnRight = crossedXEdge === 'minX'
          ? rotation.cos > 0   // minX: inside water at higher X
          : rotation.cos < 0;  // maxX: inside water at lower X

        edgeData.push({
          wallX: intersectionX,
          waterOnPositiveSide: waterOnRight,
          edgeType: 'x'
        });
      }
    }

    // Process Z edge if crossed
    if (crossedZEdge) {
      const isFrontEdge = this.isSideFaceVisible(crossedZEdge, camera);

      if (isFrontEdge) {
        allBackEdges = false;

        // Project the Z edge (runs along X)
        const zEdgeZ = crossedZEdge === 'minZ' ? waterBounds.minZ : waterBounds.maxZ;
        const edgePoint1 = this.projectToSpritePercent(
          waterBounds.minX, surfaceY, zEdgeZ,
          spriteBounds, camera, cubeSize, zoomParam
        );
        const edgePoint2 = this.projectToSpritePercent(
          waterBounds.maxX, surfaceY, zEdgeZ,
          spriteBounds, camera, cubeSize, zoomParam
        );

        const intersectionX = this.findLineHorizontalIntersection(
          edgePoint1.x, edgePoint1.y,
          edgePoint2.x, edgePoint2.y,
          horizontalClipPercent
        );

        // For Z edges: inside water direction based on sin (due to Z negation)
        const waterOnRight = crossedZEdge === 'minZ'
          ? rotation.sin > 0   // minZ: inside water at higher Z
          : rotation.sin < 0;  // maxZ: inside water at lower Z

        edgeData.push({
          wallX: intersectionX,
          waterOnPositiveSide: waterOnRight,
          edgeType: 'z'
        });
      }
    }

    // If no edges crossed or all are back edges, treat as simple case
    if (edgeData.length === 0) {
      return {
        edgeData: [],
        horizontalClipPercent,
        crosses,
        isBackEdge: allBackEdges && (crossedXEdge || crossedZEdge)
      };
    }

    // Sort edgeData by wallX for consistent polygon building
    edgeData.sort((a, b) => a.wallX - b.wallX);

    return {
      edgeData,
      horizontalClipPercent,
      crosses,
      isBackEdge: false,
      isCorner: edgeData.length === 2
    };
  }

  /**
   * Find where a line intersects a horizontal line at Y = targetY
   * Returns the X coordinate of the intersection
   */
  findLineHorizontalIntersection(x1, y1, x2, y2, targetY) {
    // If the line is horizontal (y1 == y2), return midpoint
    if (Math.abs(y2 - y1) < 0.001) {
      return (x1 + x2) / 2;
    }

    // Linear interpolation: find t where y1 + t*(y2-y1) = targetY
    const t = (targetY - y1) / (y2 - y1);

    // Clamp t to [0, 1] to stay within the line segment
    const tClamped = Math.max(0, Math.min(1, t));

    // Calculate X at this t
    return x1 + tClamped * (x2 - x1);
  }

  /**
   * Generate CSS clip-path for the submerged portion
   */
  getSubmergedClipPath(clipInfo) {
    const bottom = clipInfo.clipBottomPercent ?? 100;
    const btm = bottom.toFixed(2);

    // Fully submerged: entire band is underwater
    if (clipInfo.isFullySubmerged) {
      return `polygon(0% 0%, 100% 0%, 100% ${btm}%, 0% ${btm}%)`;
    }

    if (clipInfo.isSimpleHorizontalClip) {
      const top = clipInfo.clipTopPercent;
      return `polygon(0% ${top.toFixed(2)}%, 100% ${top.toFixed(2)}%, 100% ${btm}%, 0% ${btm}%)`;
    }

    const { clipData } = clipInfo;
    if (!clipData) {
      const top = clipInfo.clipTopPercent;
      return `polygon(0% ${top.toFixed(2)}%, 100% ${top.toFixed(2)}%, 100% ${btm}%, 0% ${btm}%)`;
    }

    // buildSubmergedPolygon handles both front edges (with edgeData) and back edges (isBackEdge=true)
    return this.buildSubmergedPolygon(clipData, bottom);
  }

  /**
   * Generate CSS clip-path for the above-water portion
   */
  getAboveWaterClipPath(clipInfo) {
    const bottom = clipInfo.clipBottomPercent ?? 100;
    const hasBand = bottom < 99.9;

    // Fully submerged: nothing visible above water (but show below-band if band-limited)
    if (clipInfo.isFullySubmerged) {
      if (hasBand) {
        const btm = bottom.toFixed(2);
        return `polygon(0% ${btm}%, 100% ${btm}%, 100% 100%, 0% 100%)`;
      }
      return 'polygon(0% 0%, 0% 0%, 0% 0%, 0% 0%)';
    }

    if (clipInfo.isSimpleHorizontalClip) {
      const top = clipInfo.clipTopPercent;
      if (!hasBand) {
        return `polygon(0% 0%, 100% 0%, 100% ${top.toFixed(2)}%, 0% ${top.toFixed(2)}%)`;
      }
      // Band: show above-surface + below-band as figure-8 polygon
      const btm = bottom.toFixed(2);
      return `polygon(0% 0%, 100% 0%, 100% ${top.toFixed(2)}%, 0% ${top.toFixed(2)}%, 0% ${btm}%, 100% ${btm}%, 100% 100%, 0% 100%)`;
    }

    const { clipData } = clipInfo;
    if (!clipData) {
      const top = clipInfo.clipTopPercent;
      if (!hasBand) {
        return `polygon(0% 0%, 100% 0%, 100% ${top.toFixed(2)}%, 0% ${top.toFixed(2)}%)`;
      }
      const btm = bottom.toFixed(2);
      return `polygon(0% 0%, 100% 0%, 100% ${top.toFixed(2)}%, 0% ${top.toFixed(2)}%, 0% ${btm}%, 100% ${btm}%, 100% 100%, 0% 100%)`;
    }

    // buildAboveWaterPolygon handles both front edges (L-shaped) and back edges (simple rectangle)
    return this.buildAboveWaterPolygon(clipData, bottom);
  }

  /**
   * Build submerged clip polygon
   * The submerged region is inside water AND below water surface
   * Shape depends on number of edges and their visibility:
   * - Back edge only: entire below-surface region is submerged (full width)
   * - Single front edge: horizontal line at surface + vertical line at wall
   * - Corner (2 edges): rectangle bounded by both walls
   */
  buildSubmergedPolygon(clipData, bottom = 100) {
    const { edgeData, horizontalClipPercent, isBackEdge, isCorner } = clipData;
    const surface = horizontalClipPercent;
    const btm = bottom.toFixed(2);

    // Back edge: entire below-surface region is behind water
    if (isBackEdge || !edgeData || edgeData.length === 0) {
      return `polygon(0% ${surface.toFixed(2)}%, 100% ${surface.toFixed(2)}%, 100% ${btm}%, 0% ${btm}%)`;
    }

    // Corner case: 2 edges - submerged region is bounded by both walls
    if (isCorner && edgeData.length === 2) {
      const edge1 = edgeData[0]; // Sorted by wallX, so this is leftmost
      const edge2 = edgeData[1]; // This is rightmost
      const wall1 = Math.max(0, Math.min(100, edge1.wallX));
      const wall2 = Math.max(0, Math.min(100, edge2.wallX));

      // Determine submerged region based on where water is relative to each wall
      // Water is "inside" when it's on the positive side for that edge
      // For a corner, the submerged region is the intersection of both "inside" regions

      if (edge1.waterOnPositiveSide && edge2.waterOnPositiveSide) {
        // Both edges: water on right → submerged is right of wall2 (the rightmost)
        return `polygon(${wall2.toFixed(2)}% ${surface.toFixed(2)}%, 100% ${surface.toFixed(2)}%, 100% ${btm}%, ${wall2.toFixed(2)}% ${btm}%)`;
      } else if (!edge1.waterOnPositiveSide && !edge2.waterOnPositiveSide) {
        // Both edges: water on left → submerged is left of wall1 (the leftmost)
        return `polygon(0% ${surface.toFixed(2)}%, ${wall1.toFixed(2)}% ${surface.toFixed(2)}%, ${wall1.toFixed(2)}% ${btm}%, 0% ${btm}%)`;
      } else if (edge1.waterOnPositiveSide && !edge2.waterOnPositiveSide) {
        // edge1: water on right, edge2: water on left → submerged is between walls
        return `polygon(${wall1.toFixed(2)}% ${surface.toFixed(2)}%, ${wall2.toFixed(2)}% ${surface.toFixed(2)}%, ${wall2.toFixed(2)}% ${btm}%, ${wall1.toFixed(2)}% ${btm}%)`;
      } else {
        // edge1: water on left, edge2: water on right → no overlap (shouldn't happen for valid corner)
        // Fall back to full width submerged
        return `polygon(0% ${surface.toFixed(2)}%, 100% ${surface.toFixed(2)}%, 100% ${btm}%, 0% ${btm}%)`;
      }
    }

    // Single edge: use vertical clip at wall intersection
    const edge = edgeData[0];
    const wallX = Math.max(0, Math.min(100, edge.wallX));

    if (edge.waterOnPositiveSide) {
      return `polygon(${wallX.toFixed(2)}% ${surface.toFixed(2)}%, 100% ${surface.toFixed(2)}%, 100% ${btm}%, ${wallX.toFixed(2)}% ${btm}%)`;
    } else {
      return `polygon(0% ${surface.toFixed(2)}%, ${wallX.toFixed(2)}% ${surface.toFixed(2)}%, ${wallX.toFixed(2)}% ${btm}%, 0% ${btm}%)`;
    }
  }

  /**
   * Build above-water clip polygon
   * Shape depends on number of edges and their visibility:
   * - Back edge only: simple rectangle (only above surface, entire width)
   * - Single front edge: L-shaped (outside water full height + inside water above surface only)
   * - Corner (2 edges): U-shaped or more complex, showing outside regions at full height
   */
  buildAboveWaterPolygon(clipData, bottom = 100) {
    const { edgeData, horizontalClipPercent, isBackEdge, isCorner } = clipData;
    const surface = horizontalClipPercent;
    const hasBand = bottom < 99.9;
    const btm = bottom.toFixed(2);

    // Helper: append below-band full-width rectangle via zero-width bridge.
    // Creates a figure-8 polygon that CSS fills correctly with nonzero winding rule.
    const appendBelowBand = (polygon) => {
      if (!hasBand) return polygon;
      return polygon.replace(/\)$/, `, 0% ${btm}%, 100% ${btm}%, 100% 100%, 0% 100%)`);
    };

    // Back edge: entire below-surface region is behind water
    if (isBackEdge || !edgeData || edgeData.length === 0) {
      return appendBelowBand(`polygon(0% 0%, 100% 0%, 100% ${surface.toFixed(2)}%, 0% ${surface.toFixed(2)}%)`);
    }

    // Corner case: 2 edges - above-water shows full height for outside regions
    if (isCorner && edgeData.length === 2) {
      const edge1 = edgeData[0]; // Leftmost wall
      const edge2 = edgeData[1]; // Rightmost wall
      const wall1 = Math.max(0, Math.min(100, edge1.wallX));
      const wall2 = Math.max(0, Math.min(100, edge2.wallX));

      // The visible region is complement of submerged: everything except the "inside both" region below surface
      let base;
      if (edge1.waterOnPositiveSide && edge2.waterOnPositiveSide) {
        // Submerged: right of wall2 → visible below surface: left of wall2
        base = `polygon(0% 0%, 100% 0%, 100% ${surface.toFixed(2)}%, ${wall2.toFixed(2)}% ${surface.toFixed(2)}%, ${wall2.toFixed(2)}% ${btm}%, 0% ${btm}%)`;
      } else if (!edge1.waterOnPositiveSide && !edge2.waterOnPositiveSide) {
        // Submerged: left of wall1 → visible below surface: right of wall1
        base = `polygon(0% 0%, 100% 0%, 100% ${btm}%, ${wall1.toFixed(2)}% ${btm}%, ${wall1.toFixed(2)}% ${surface.toFixed(2)}%, 0% ${surface.toFixed(2)}%)`;
      } else if (edge1.waterOnPositiveSide && !edge2.waterOnPositiveSide) {
        // Submerged: between wall1 and wall2 → visible below surface: left of wall1 OR right of wall2
        // U-shaped: full width top, left column full height, right column full height, middle only above surface
        base = `polygon(0% 0%, 100% 0%, 100% ${btm}%, ${wall2.toFixed(2)}% ${btm}%, ${wall2.toFixed(2)}% ${surface.toFixed(2)}%, ${wall1.toFixed(2)}% ${surface.toFixed(2)}%, ${wall1.toFixed(2)}% ${btm}%, 0% ${btm}%)`;
      } else {
        // edge1: water on left, edge2: water on right → theoretically no submerged region
        base = `polygon(0% 0%, 100% 0%, 100% ${btm}%, 0% ${btm}%)`;
      }
      return appendBelowBand(base);
    }

    // Single edge: L-shaped clip
    const edge = edgeData[0];
    const wallX = Math.max(0, Math.min(100, edge.wallX));

    let base;
    if (edge.waterOnPositiveSide) {
      // Water on right → left is outside (full height), right is inside (above surface only)
      base = `polygon(0% 0%, 100% 0%, 100% ${surface.toFixed(2)}%, ${wallX.toFixed(2)}% ${surface.toFixed(2)}%, ${wallX.toFixed(2)}% ${btm}%, 0% ${btm}%)`;
    } else {
      // Water on left → right is outside (full height), left is inside (above surface only)
      base = `polygon(0% 0%, 100% 0%, 100% ${btm}%, ${wallX.toFixed(2)}% ${btm}%, ${wallX.toFixed(2)}% ${surface.toFixed(2)}%, 0% ${surface.toFixed(2)}%)`;
    }
    return appendBelowBand(base);
  }

  /**
   * Calculate clip information for a specific water volume (group of connected cuboids).
   * This is the core clip calculation, used by both single and multi-volume paths.
   * @param {Object} character - The character
   * @param {Object} camera - The camera
   * @param {Cuboid[]} volumeCuboids - Water cuboids in this volume
   * @returns {Object|null} Clip info for this volume, or null if no valid intersection
   */
  calculateClipInfoForVolume(character, camera, volumeCuboids) {
    if (volumeCuboids.length === 0) return null;

    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const cubeSize = camera.CUBE_SIZE * zoom;
    const zoomParam = isAndroid ? zoom : 1;

    const halfWidth = character.width / 2;
    const halfDepth = character.depth / 2;

    const charAABB = {
      minX: character.x - halfWidth,
      minY: character.y,
      minZ: character.z - halfDepth,
      maxX: character.x + halfWidth,
      maxY: character.y + character.height,
      maxZ: character.z + halfDepth
    };

    // Calculate union of this volume's water intersections
    let unionMinX = Infinity, unionMinY = Infinity, unionMinZ = Infinity;
    let unionMaxX = -Infinity, unionMaxY = -Infinity, unionMaxZ = -Infinity;

    for (const cuboid of volumeCuboids) {
      const waterAABB = {
        minX: cuboid.x, minY: cuboid.y, minZ: cuboid.z,
        maxX: cuboid.x + cuboid.width,
        maxY: cuboid.y + cuboid.height,
        maxZ: cuboid.z + cuboid.depth
      };

      const intersection = this.aabbIntersection(charAABB, waterAABB);
      unionMinX = Math.min(unionMinX, intersection.minX);
      unionMinY = Math.min(unionMinY, intersection.minY);
      unionMinZ = Math.min(unionMinZ, intersection.minZ);
      unionMaxX = Math.max(unionMaxX, intersection.maxX);
      unionMaxY = Math.max(unionMaxY, intersection.maxY);
      unionMaxZ = Math.max(unionMaxZ, intersection.maxZ);
    }

    if (unionMaxX - unionMinX <= 0.01 || unionMaxY - unionMinY <= 0.01 || unionMaxZ - unionMinZ <= 0.01) {
      return null;
    }

    const spriteBounds = this.getSpriteScreenBounds(character, camera);
    const surfaceY = unionMaxY;

    // Calculate which edges the character crosses
    const EDGE_THRESHOLD = 0.01;
    const crossesMinX = unionMinX > charAABB.minX + EDGE_THRESHOLD;
    const crossesMaxX = unionMaxX < charAABB.maxX - EDGE_THRESHOLD;
    const crossesMinZ = unionMinZ > charAABB.minZ + EDGE_THRESHOLD;
    const crossesMaxZ = unionMaxZ < charAABB.maxZ - EDGE_THRESHOLD;

    // Calculate horizontal clip percentage (water surface)
    const characterHeadY = character.y + character.height;
    const characterFeetY = character.y;
    const clampedSurfaceY = Math.max(characterFeetY, Math.min(characterHeadY, surfaceY));
    // Raw clip percent in collision-box space (for edge intersection math)
    const rawClipPercent = ((characterHeadY - clampedSurfaceY) / character.height) * 100;

    // Adjust for sprite Y offset (see calculateClipInfo for explanation)
    const spriteOffsetY = character.sprite?.spriteOffset?.y ?? -0.88;
    const offsetDelta = spriteOffsetY - (-0.88);
    let horizontalClipPercent = rawClipPercent;
    if (Math.abs(offsetDelta) > 0.001) {
      horizontalClipPercent += (offsetDelta / character.height) * 100;
    }

    // Calculate clipBottomPercent: where volume BOTTOM maps on sprite (0%=head, 100%=feet)
    // For volumes that extend to/below character feet, this is 100% (no band clipping).
    // For volumes above the feet (e.g. upper cuboid in a stack), this limits the
    // submerged fragment to only the portion within this volume's Y range.
    const volumeBottomY = unionMinY;
    const clampedBottomY = Math.max(characterFeetY, Math.min(characterHeadY, volumeBottomY));
    let clipBottomPercent = ((characterHeadY - clampedBottomY) / character.height) * 100;
    if (Math.abs(offsetDelta) > 0.001) {
      clipBottomPercent += (offsetDelta / character.height) * 100;
    }
    clipBottomPercent = Math.max(0, Math.min(100, clipBottomPercent));

    // If fully inside water horizontally, use simple horizontal clip
    if (!crossesMinX && !crossesMaxX && !crossesMinZ && !crossesMaxZ) {
      if (horizontalClipPercent <= 0.1) {
        // Fully submerged - entire sprite is underwater
        return {
          waterCuboids: volumeCuboids,
          clipTopPercent: 0,
          clipBottomPercent,
          isSimpleHorizontalClip: true,
          isFullySubmerged: true,
          surfaceY
        };
      }

      return {
        waterCuboids: volumeCuboids,
        clipTopPercent: horizontalClipPercent,
        clipBottomPercent,
        isSimpleHorizontalClip: true,
        surfaceY
      };
    }

    // Character crosses water edges - need to build proper clip polygon
    const waterBounds = {
      minX: unionMinX, maxX: unionMaxX,
      minZ: unionMinZ, maxZ: unionMaxZ
    };

    // Pass rawClipPercent (un-adjusted) so edge intersection math uses the same
    // coordinate system as projectToSpritePercent. Apply offset afterward.
    const clipData = this.buildEdgeClipData(
      character, charAABB, waterBounds, surfaceY,
      spriteBounds, camera, cubeSize, zoomParam,
      { crossesMinX, crossesMaxX, crossesMinZ, crossesMaxZ },
      rawClipPercent
    );
    // Apply offset to stored clip percent for CSS polygon construction
    clipData.horizontalClipPercent = horizontalClipPercent;
    clipData.clipBottomPercent = clipBottomPercent;

    return {
      waterCuboids: volumeCuboids,
      clipTopPercent: horizontalClipPercent,
      clipBottomPercent,
      isSimpleHorizontalClip: false,
      clipData,
      surfaceY
    };
  }

  /**
   * Calculate clip information for multiple water volumes.
   * Groups touching water cuboids into volumes and calculates clips for each.
   * @param {Object} character - The character
   * @param {Object} camera - The camera
   * @returns {Object[]|null} Array of clip infos (one per volume), or null if no water
   */
  calculateClipInfoMultiple(character, camera) {
    const waterCuboids = this.findIntersectingWaterCuboids(character);
    if (waterCuboids.length === 0) return null;

    const volumes = this.groupConnectedWaterVolumes(waterCuboids);

    const clipInfos = [];
    for (const volume of volumes) {
      const clipInfo = this.calculateClipInfoForVolume(character, camera, volume);
      if (clipInfo) {
        clipInfo.volumeId = this.getVolumeId(volume);
        clipInfos.push(clipInfo);
      }
    }

    return clipInfos.length > 0 ? clipInfos : null;
  }

  /**
   * Update water intersection for a character and apply clip paths
   */
  updateIntersection(character, camera) {
    const clipInfo = this.calculateClipInfo(character, camera);

    if (!clipInfo) {
      if (character.sprite) {
        character.sprite.clearWaterClip();
      }
      return null;
    }

    if (character.sprite) {
      character.sprite.applyWaterClip(
        this.getAboveWaterClipPath(clipInfo),
        this.getSubmergedClipPath(clipInfo)
      );
    }

    return {
      waterCuboids: clipInfo.waterCuboids,
      surfaceY: clipInfo.surfaceY
    };
  }

  /**
   * Get an impostor's screen-space bounding box, accounting for the cubeSize/2
   * rendering offset that Impostor.update() applies for cuboid grid alignment.
   * @param {Impostor} impostor
   * @param {Object} camera
   * @returns {Object} Screen bounds { left, right, top, bottom, width, height, centerX }
   */
  getImpostorScreenBounds(impostor, camera) {
    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const effectiveCubeSize = camera.CUBE_SIZE * zoom;
    const zoomParam = isAndroid ? zoom : 1;

    // Use raw worldToScreen for the base position (NO cubeSize/2 offset).
    // Both impostors and water cuboids have the same cubeSize/2 rendering offset,
    // so they cancel out in percentage calculations. Using raw positions ensures
    // projectToSpritePercent and horizontalClipPercent are in the same coordinate
    // system — the cs/2 offset cancels between sprite bounds and projected water points.
    const worldX = impostor.x * effectiveCubeSize;
    const worldZ = -impostor.z * effectiveCubeSize;
    const worldY = impostor.y * effectiveCubeSize;
    const baseScreen = Transform.worldToScreen(worldX, worldY, worldZ, camera, zoomParam);

    const displayHeight = impostor.scale * effectiveCubeSize;
    const aspectRatio = impostor.spriteWidth / impostor.spriteHeight;
    const displayWidth = displayHeight * aspectRatio;

    const top = baseScreen.y - displayHeight;
    const bottom = baseScreen.y;

    return {
      left: baseScreen.x - displayWidth / 2,
      right: baseScreen.x + displayWidth / 2,
      top,
      bottom,
      width: displayWidth,
      height: displayHeight,
      centerX: baseScreen.x
    };
  }

  /**
   * Calculate clip info for an impostor in a water volume.
   * Similar to calculateClipInfoForVolume but handles the cubeSize/2 rendering
   * offset that impostors use, and doesn't need spriteOffset adjustments.
   * @param {Impostor} impostor
   * @param {Object} camera
   * @param {Cuboid[]} volumeCuboids - Water cuboids in this volume
   * @returns {Object|null} Clip info for this volume
   */
  calculateImpostorClipInfoForVolume(impostor, camera, volumeCuboids) {
    if (volumeCuboids.length === 0) return null;

    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const cubeSize = camera.CUBE_SIZE * zoom;
    const zoomParam = isAndroid ? zoom : 1;

    // Use visual extent for clip AABB so edge detection works when the
    // billboard sprite extends past the sort footprint (1x1).
    const visualHeight = impostor.scale;
    const visualWidth = visualHeight * (impostor.spriteWidth / impostor.spriteHeight);
    const clipHalfW = Math.max(impostor.sortWidth, visualWidth) / 2;
    const clipHalfD = Math.max(impostor.sortDepth, visualWidth) / 2;

    const impAABB = {
      minX: impostor.x - clipHalfW,
      minY: impostor.y,
      minZ: impostor.z - clipHalfD,
      maxX: impostor.x + clipHalfW,
      maxY: impostor.y + visualHeight,
      maxZ: impostor.z + clipHalfD
    };

    // Calculate union of this volume's water intersections (same as character version)
    let unionMinX = Infinity, unionMinY = Infinity, unionMinZ = Infinity;
    let unionMaxX = -Infinity, unionMaxY = -Infinity, unionMaxZ = -Infinity;

    for (const cuboid of volumeCuboids) {
      const waterAABB = {
        minX: cuboid.x, minY: cuboid.y, minZ: cuboid.z,
        maxX: cuboid.x + cuboid.width,
        maxY: cuboid.y + cuboid.height,
        maxZ: cuboid.z + cuboid.depth
      };

      const intersection = this.aabbIntersection(impAABB, waterAABB);
      unionMinX = Math.min(unionMinX, intersection.minX);
      unionMinY = Math.min(unionMinY, intersection.minY);
      unionMinZ = Math.min(unionMinZ, intersection.minZ);
      unionMaxX = Math.max(unionMaxX, intersection.maxX);
      unionMaxY = Math.max(unionMaxY, intersection.maxY);
      unionMaxZ = Math.max(unionMaxZ, intersection.maxZ);
    }

    if (unionMaxX - unionMinX <= 0.01 || unionMaxY - unionMinY <= 0.01 || unionMaxZ - unionMinZ <= 0.01) {
      return null;
    }

    // Use impostor-specific screen bounds (with cubeSize/2 offset)
    const spriteBounds = this.getImpostorScreenBounds(impostor, camera);
    const surfaceY = unionMaxY;

    // Edge crossing detection — same logic as character: does the intersection
    // NOT reach the impostor AABB edge? If so, the impostor crosses that water edge.
    const EDGE_THRESHOLD = 0.01;
    const crossesMinX = unionMinX > impAABB.minX + EDGE_THRESHOLD;
    const crossesMaxX = unionMaxX < impAABB.maxX - EDGE_THRESHOLD;
    const crossesMinZ = unionMinZ > impAABB.minZ + EDGE_THRESHOLD;
    const crossesMaxZ = unionMaxZ < impAABB.maxZ - EDGE_THRESHOLD;

    // Horizontal clip percent from world coordinates — use visual height (scale).
    // No cubeSize/2 offset correction needed: both impostors and water cuboids
    // have the same cubeSize/2 rendering offset, so they cancel out.
    const impostorHeadY = impostor.y + visualHeight;
    const impostorFeetY = impostor.y;
    const clampedSurfaceY = Math.max(impostorFeetY, Math.min(impostorHeadY, surfaceY));
    let horizontalClipPercent = ((impostorHeadY - clampedSurfaceY) / visualHeight) * 100;

    // Calculate clipBottomPercent: where volume BOTTOM maps on sprite (band clipping)
    const volumeBottomY = unionMinY;
    const clampedBottomY = Math.max(impostorFeetY, Math.min(impostorHeadY, volumeBottomY));
    let clipBottomPercent = ((impostorHeadY - clampedBottomY) / visualHeight) * 100;
    clipBottomPercent = Math.max(0, Math.min(100, clipBottomPercent));

    // Simple horizontal clip (no edge crossings)
    if (!crossesMinX && !crossesMaxX && !crossesMinZ && !crossesMaxZ) {
      const clampedClip = Math.max(0, Math.min(100, horizontalClipPercent));
      if (clampedClip <= 2) {
        return {
          waterCuboids: volumeCuboids,
          clipTopPercent: 0,
          clipBottomPercent,
          isSimpleHorizontalClip: true,
          isFullySubmerged: true,
          surfaceY
        };
      }

      return {
        waterCuboids: volumeCuboids,
        clipTopPercent: clampedClip,
        clipBottomPercent,
        isSimpleHorizontalClip: true,
        surfaceY
      };
    }

    // Edge clips — use intersection bounds (same as character)
    const waterBounds = {
      minX: unionMinX, maxX: unionMaxX,
      minZ: unionMinZ, maxZ: unionMaxZ
    };

    // Pass UNCLAMPED horizontalClipPercent so findLineHorizontalIntersection
    // gets the correct t value (offset sprite bounds shift edge point Y% by the
    // same amount, so unclamped clip percent stays consistent with them)
    const clipData = this.buildEdgeClipData(
      null, impAABB, waterBounds, surfaceY,
      spriteBounds, camera, cubeSize, zoomParam,
      { crossesMinX, crossesMaxX, crossesMinZ, crossesMaxZ },
      horizontalClipPercent
    );

    // Now clamp for CSS polygon construction
    const clampedClip = Math.max(0, Math.min(100, horizontalClipPercent));
    clipData.horizontalClipPercent = clampedClip;
    clipData.clipBottomPercent = clipBottomPercent;

    return {
      waterCuboids: volumeCuboids,
      clipTopPercent: clampedClip,
      clipBottomPercent,
      isSimpleHorizontalClip: false,
      clipData,
      surfaceY
    };
  }
}
